import React, { useEffect, useState } from "react";
import API from "../api/axios";
import {
  User,
  Mail,
  Phone,
  Briefcase,
  Users,
  DollarSign,
  Gift,
  Calendar,
  Copy,
  Share2,
  Loader2,
  Shield,
  Hash,
  UserCircle,
  Wallet,
  X
} from "lucide-react";

export default function Profile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [shared, setShared] = useState(false);

  const [walletModal, setWalletModal] = useState(false);
  const [newWalletAddress, setNewWalletAddress] = useState("");
  const [walletUpdating, setWalletUpdating] = useState(false);
  const [profileUpdating, setProfileUpdating] = useState(false);



  const [qrModal, setQrModal] = useState(false);
  const [newQrFile, setNewQrFile] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [qrUpdating, setQrUpdating] = useState(false);

  const [refModal, setRefModal] = useState(false);
  const [refList, setRefList] = useState([]);
  const [refLoading, setRefLoading] = useState(false);

  const [editModal, setEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    name: "",
    email: "",
    phone: "",
    gender: "",
    profession: "",
  });


  const openEditModal = () => {
    setEditForm({
      name: profile.name,
      email: profile.email,
      phone: profile.phone,
      gender: profile.gender || "",
      profession: profile.profession || "",
    });
    setEditModal(true);
  };


  const saveProfile = async () => {
    try {
      setProfileUpdating(true);
      const res = await API.put(`/auth/update-profile/${profile._id}`, editForm);

      setEditModal(false);
      fetchProfile(); // refresh data
    } catch (err) {
      setError(err.response?.data?.message || "Update failed");
    } finally {
      setProfileUpdating(false);
    }
  };



  const loadReferrals = async () => {
    setRefLoading(true);
    try {
      const res = await API.get(`/auth/referrals/${profile._id}`);
      setRefList(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setRefLoading(false);
    }
  };


  const handleQrUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setNewQrFile(file);
    setQrPreview(URL.createObjectURL(file));
  };



  const fetchProfile = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/wallet/profile");
      setProfile(res.data);
    } catch (err) {
      setError("Failed to load profile. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchProfile();
  }, []);


  const saveQrImage = async () => {
    if (!newQrFile) {
      setError("Please select a QR code image.");
      return;
    }

    setQrUpdating(true);
    setError("");

    try {
      const fd = new FormData();
      fd.append("qrImage", newQrFile);

      await API.put(`/auth/update-qr/${profile._id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setQrModal(false);
      fetchProfile(); // refresh data
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update QR code");
    } finally {
      setQrUpdating(false);
    }
  };



  const openWalletModal = () => {
    setNewWalletAddress(profile.walletAddress || "");
    setWalletModal(true);
  };


  const saveWalletAddress = async () => {
    if (!newWalletAddress.trim()) {
      setError("Wallet address is required");
      return;
    }

    setWalletUpdating(true);
    setError("");

    try {
      await API.put(`/auth/update-wallet-address/${profile._id}`, {
        walletAddress: newWalletAddress.trim(),
      });

      setWalletModal(false);
      fetchProfile(); // refresh
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update address");
    } finally {
      setWalletUpdating(false);
    }
  };


  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });

  const signupLink = (code) =>
    `${window.location.origin}/signup?ref=${encodeURIComponent(code || "")}`;

  const copyCode = async () => {
    if (!profile?.referralCode) return;
    try {
      await navigator.clipboard.writeText(profile.referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { }
  };

  const shareCode = async () => {
    if (!profile?.referralCode) return;

    const code = profile.referralCode;
    const link = signupLink(code);
    const text = `Hey! Use my referral code "${code}" to sign up: ${link}`;

    try {
      if (navigator.share) {
        await navigator.share({ title: "Join with my referral code", text, url: link });
      } else {
        await navigator.clipboard.writeText(text);
      }
      setShared(true);
      setTimeout(() => setShared(false), 1500);
    } catch { }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col">

      <div className="flex-1 p-6 md:p-10">
        <div className="max-w-5xl mx-auto">

          {/* Loader */}
          {loading && (
            <div className="bg-white rounded-3xl shadow-xl p-20 text-center border border-gray-200 animate-fadeIn">
              <Loader2 className="w-14 h-14 animate-spin text-gray-700 mx-auto" />
              <p className="text-gray-600 mt-6 text-lg">Loading your profile...</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-8 py-6 rounded-3xl text-center shadow-lg animate-fadeIn">
              <p className="text-xl font-semibold">{error}</p>
              <button
                onClick={fetchProfile}
                className="mt-5 px-8 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition shadow-md"
              >
                Retry
              </button>
            </div>
          )}

          {/* Profile */}
          {!loading && !error && profile && (
            <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-10 border border-gray-200 animate-fadeIn">

              {/* Header: Name + Status */}
              <div className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  {profile.name}
                </h2>
                <div className="flex justify-center items-center gap-2 mt-3">
                  <Shield
                    className={`w-6 h-6 ${profile.status ? "text-emerald-600" : "text-yellow-600"
                      }`}
                  />
                  <span
                    className={`text-lg font-medium ${profile.status ? "text-emerald-600" : "text-yellow-600"
                      }`}
                  >
                    {profile.status ? "Active Account" : "Inactive Account"}
                  </span>
                </div>
              </div>

              {/* Referral Section */}
              <div className="bg-gray-100 border border-gray-300 rounded-3xl p-6 mb-10">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-6">

                  {/* Code */}
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-800 rounded-xl">
                      <Gift className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Your Referral Code</p>
                      <p className="text-2xl font-bold text-gray-800 tracking-widest">
                        {profile.referralCode}
                      </p>

                      <button
                        onClick={() => {
                          loadReferrals();
                          setRefModal(true);
                        }}
                        className="mt-2 px-4 py-2 bg-gray-900 text-white rounded-xl text-sm hover:bg-black"
                      >
                        View Referrals
                      </button>

                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-4 w-full sm:w-auto justify-center sm:justify-end">
                    <button
                      onClick={copyCode}
                      className="flex items-center gap-2 px-5 py-3 rounded-xl border border-gray-400 text-gray-700 hover:bg-gray-200 transition font-medium w-full sm:w-auto"
                    >
                      <Copy className="w-5 h-5" />
                      {copied ? "Copied!" : "Copy Code"}
                    </button>

                    <button
                      onClick={shareCode}
                      className="flex items-center gap-2 px-5 py-3 bg-gray-900 text-white rounded-xl hover:bg-black transition font-medium w-full sm:w-auto"
                    >
                      <Share2 className="w-5 h-5" />
                      {shared ? "Shared!" : "Share"}
                    </button>
                  </div>
                </div>


                <div className="flex flex-row justify-between">
                  <p className="mt-4 text-xs text-gray-500 break-all text-center sm:text-left">
                    Invite Link: {signupLink(profile.referralCode)}
                  </p>

                  <button
                    onClick={openEditModal}
                    className="mt-3 px-5 py-2 bg-gray-600 text-white rounded-xl hover:bg-gray-700 text-sm"
                  >
                    Edit Profile
                  </button>

                </div>


              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">

                {/* Reusable Card */}
                {[
                  {
                    label: "Email",
                    value: profile.email,
                    icon: Mail,
                  },
                  {
                    label: "Phone",
                    value: profile.phone,
                    icon: Phone,
                  },
                  {
                    label: "Gender",
                    value: profile.gender || "—",
                    icon: Users,
                  },
                  {
                    label: "Profession",
                    value: profile.profession || "—",
                    icon: Briefcase,
                  },
                ].map((item, i) => (
                  <div
                    key={i}
                    className="bg-gray-50 rounded-2xl p-5 border border-gray-200 flex items-center gap-4"
                  >
                    <div className="p-3 bg-gray-800 rounded-xl">
                      <item.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">{item.label}</p>
                      <p className="font-bold text-gray-800">{item.value}</p>
                    </div>
                  </div>
                ))}

                {/* Balance */}
                <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-200">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-emerald-600 rounded-xl">
                      <Wallet className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Wallet Balance</p>
                      <p className="text-2xl font-bold text-emerald-700 flex items-center gap-1">
                        <DollarSign className="w-7 h-7" />
                        {Number(profile.balance).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Joined */}
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200">
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-gray-800 rounded-xl">
                      <Calendar className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Member Since</p>
                      <p className="font-bold text-gray-800">
                        {formatDate(profile.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 col-span-1 sm:col-span-2">
                  <div className="flex items-center justify-between">

                    {/* Left content */}
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-gray-800 rounded-xl">
                        <Wallet className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Wallet Address</p>
                        <p className="font-bold text-gray-800 break-all">
                          {profile.walletAddress || "Not set"}
                        </p>
                      </div>
                    </div>

                    {/* Edit Button */}
                    <button
                      onClick={openWalletModal}
                      className="px-4 py-2 bg-gray-900 text-white rounded-xl hover:bg-black transition text-sm"
                    >
                      Update
                    </button>
                  </div>
                </div>


                {/* QR Code Card */}
                <div className="bg-gray-50 rounded-2xl p-5 border border-gray-200 col-span-1 sm:col-span-2">
                  <div className="flex items-center justify-between">

                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-gray-800 rounded-xl">
                        <Hash className="w-6 h-6 text-white" />
                      </div>

                      <div>
                        <p className="text-sm text-gray-600">QR Code</p>

                        {profile.qrImage ? (
                          <img
                            src={`https://api.kestren.com${profile.qrImage}`}
                            className="w-32 h-32 rounded-xl border mt-2 object-cover"
                            alt="QR"
                          />
                        ) : (
                          <p className="font-bold text-gray-800">Not uploaded</p>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => setQrModal(true)}
                      className="px-4 py-2 bg-gray-900 text-white rounded-xl hover:bg-black transition text-sm"
                    >
                      Update
                    </button>
                  </div>
                </div>




                {/* Admin Role */}
                {profile.role === "admin" && (
                  <div className="bg-amber-50 rounded-2xl p-5 border border-amber-300 col-span-1 sm:col-span-2">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-amber-600 rounded-xl">
                        <UserCircle className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Account Role</p>
                        <p className="text-xl font-bold text-amber-700">
                          Administrator
                        </p>
                      </div>
                    </div>
                  </div>
                )}

              </div>

            </div>
          )}
        </div>
      </div>

      {walletModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative">

            <button
              onClick={() => setWalletModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">
              Update Wallet Address
            </h2>

            <p className="text-center text-gray-600 mb-6">
              Enter your correct crypto wallet address.
            </p>

            <input
              type="text"
              value={newWalletAddress}
              onChange={(e) => setNewWalletAddress(e.target.value)}
              placeholder="Enter your wallet address"
              className="w-full px-5 py-4 border border-gray-300 rounded-2xl text-lg focus:ring-2 focus:ring-blue-600 outline-none"
            />

            {error && <p className="text-red-600 mt-2">{error}</p>}

            <button
              onClick={saveWalletAddress}
              disabled={walletUpdating}
              className="w-full py-4 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl shadow-lg flex justify-center"
            >
              {walletUpdating ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                "Save Wallet Address"
              )}
            </button>

          </div>
        </div>
      )}


      {/* QR Modal */}
      {qrModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative">

            <button
              onClick={() => setQrModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">
              Update QR Code
            </h2>

            {/* File Input */}
            <input
              type="file"
              accept="image/*"
              onChange={handleQrUpload}
              className="w-full px-5 py-4 border border-gray-300 rounded-2xl"
            />

            {/* Preview */}
            {qrPreview && (
              <div className="mt-4 flex justify-center">
                <img
                  src={qrPreview}
                  className="w-40 h-40 rounded-2xl border object-cover"
                  alt="QR Preview"
                />
              </div>
            )}

            {error && <p className="text-red-600 mt-2">{error}</p>}

            <button
              onClick={saveQrImage}
              disabled={qrUpdating}
              className="w-full py-4 mt-6 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-2xl flex justify-center"
            >
              {qrUpdating ? (
                <Loader2 className="w-6 h-6 animate-spin" />
              ) : (
                "Save QR Code"
              )}
            </button>

          </div>
        </div>
      )}


      {refModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-8 relative">

            <button
              onClick={() => setRefModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-2"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold text-gray-800 text-center mb-4">
              Referred Users
            </h2>

            {refLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 className="w-10 h-10 animate-spin text-gray-700" />
              </div>
            ) : refList.length === 0 ? (
              <p className="text-center text-gray-600 text-lg">No referrals yet.</p>
            ) : (
              <div className="max-h-80 overflow-y-auto space-y-4">
                {refList.map((user, index) => (
                  <div
                    key={index}
                    className="p-4 bg-gray-100 border border-gray-300 rounded-xl flex items-center justify-between"
                  >
                    <div>
                      <p className="font-bold text-gray-800">{user.name}</p>
                      <p className="text-gray-600 text-sm">{user.email}</p>
                    </div>
                    <p className="text-gray-500 text-xs">
                      {new Date(user.createdAt).toLocaleDateString()}
                    </p>
                    <div className="mt-3 flex justify-start items-center gap-2">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="font-semibold text-green-700">
                        {Number(user.balance || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      )}


      {editModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-3xl max-w-lg w-full p-8 relative">

            <button
              onClick={() => setEditModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>

            <h2 className="text-2xl font-bold text-center mb-6">Edit Profile</h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

              <div>
                <label className="text-sm font-semibold">Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Phone</label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 mt-1"
                />
              </div>


              <div>
                <label className="text-sm font-semibold">Gender</label>
                <input
                  type="text"
                  value={editForm.gender}
                  onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-semibold">Profession</label>
                <input
                  type="text"
                  value={editForm.profession}
                  onChange={(e) => setEditForm({ ...editForm, profession: e.target.value })}
                  className="w-full border rounded-xl px-4 py-3 mt-1"
                />
              </div>

            </div>

            <button
              onClick={saveProfile}
              className="w-full mt-6 py-4 bg-gray-600 text-white rounded-xl font-bold hover:bg-gray-700"
            >
              {profileUpdating ? 'Saving..' : 'Save Changes'}
            </button>

          </div>
        </div>
      )}


    </div>
  );
}
